/**
 * Technical Audit Issues Page
 *
 * Unified issue management interface for site audits.
 * Displays all detected issues in a filterable, sortable table with:
 * - Before/after diff views for each issue type
 * - Bulk skip operations
 * - Apply reviewed fixes to WordPress
 *
 * Replaces the 12-task-card layout with a streamlined table view.
 */

import { Alert, Button, Empty, message } from "antd";
import {
	ArrowLeft,
	RefreshCw,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	X,
	Filter,
	Search,
	Check,
	ChevronsUpDown,
	Loader2,
	Sparkles,
} from "lucide-react";
import { useCallback, useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	ApplyFixPreviewDialog,
	AuditExportSplitButton,
	BulkActionBar,
	IssueDiffDrawer,
	IssueTable,
	IssuesByPagePanel,
	VerifyResultModal,
	type VerifyFixResult,
} from "@/components/TechnicalAudit";
import { AuditOverview } from "@/components/TechnicalAudit/AuditOverview";
import { computeHealthScore } from "@/components/TechnicalAudit/healthScore";
import { TicketsBoard } from "@/components/TechnicalAudit/TicketsBoard";
import { DuplicateGroupsPanel } from "@/components/TechnicalAudit/DuplicateGroupsPanel";
import {
	ConditionFilter,
	isActiveCondition,
	newConditionId,
	type FilterCondition,
} from "@/components/shared/ConditionFilter";
import {
	compileConditions,
	buildAuditFilterFields,
} from "@/components/TechnicalAudit/filterConditions";
import { exportCSV, exportPDF } from "@/components/TechnicalAudit/exportAuditReport";
import {
	ISSUE_TYPE_LABELS,
	getIssueTypeFilterLabel,
	getIssueTypeFilterSearchValue,
} from "@/types/technicalAudit";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	getIssueTypeHelp,
	isDirectApplyIssueType,
} from "@/components/TechnicalAudit/issueTypeHelp";
import { useIssueSelection } from "@/hooks/useIssueSelection";
import {
	useApplySingleFix,
	useGenerateIssueSuggestion,
	useAuditRuns,
	useVerifyFix,
	useAuditRun,
	useBulkUpdateIssues,
	useUpdateIssue,
	useTechnicalAuditIssues,
	useReverifyStale,
	useManualFixTickets,
} from "@/hooks/useTechnicalAuditIssues";

import { useTenantStore } from "@/stores/useTenantStore";
import { useWorkspaceStore } from "@/stores/useWorkspaceStore";
import { cn } from "@/lib/utils";
import type {
	IssueFilters,
	PageIssue,
	IssueStatus,
	IssueType,
	IssuesSummary,
	ProposedFix,
} from "@/types/technicalAudit";

function hasGeneratedSuggestion(issue: PageIssue | null | undefined): boolean {
	return Boolean(issue?.approved_value || issue?.proposed_fix || issue?.proposed_value);
}

// Stat Card Component
interface StatCardProps {
	label: string;
	count: number;
	variant: "critical" | "warning" | "info";
	icon?: React.ReactNode;
}

function StatCard({ label, count, variant }: StatCardProps) {
	const variantStyles = {
		critical: "bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400",
		warning: "bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400",
		info: "bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400",
	};

	const numberStyles = {
		critical: "text-red-600 dark:text-red-400",
		warning: "text-amber-600 dark:text-amber-400",
		info: "text-blue-600 dark:text-blue-400",
	};

	return (
		<div
			className={cn(
				"flex flex-col items-center justify-center px-6 py-4 rounded-xl border shadow-sm",
				variantStyles[variant],
			)}
		>
			<span className={cn("text-3xl font-bold", numberStyles[variant])}>
				{count}
			</span>
			<span className="text-sm font-medium mt-1">{label}</span>
		</div>
	);
}

// Summary Header with Stat Cards
interface IssuesSummaryHeaderProps {
	summary: IssuesSummary;
}

function IssuesSummaryHeader({ summary }: IssuesSummaryHeaderProps) {
	return (
		<div className="px-6 py-5 bg-card border-b">
			<div className="flex items-center justify-between mb-4">
				<div>
					<h2 className="text-lg font-semibold text-foreground">Audit Summary</h2>
					<p className="text-sm text-muted-foreground mt-0.5">
						{summary.total} {summary.total === 1 ? "issue" : "issues"} found across all pages
					</p>
				</div>
			</div>
			<div className="grid grid-cols-3 gap-4">
				<StatCard
					label="Critical"
					count={summary.by_severity.critical}
					variant="critical"
				/>
				<StatCard
					label="Warnings"
					count={summary.by_severity.warning}
					variant="warning"
				/>
				<StatCard
					label="Info"
					count={summary.by_severity.info}
					variant="info"
				/>
			</div>
		</div>
	);
}

function IssuesLoadingSkeleton() {
	return (
		<div className="space-y-3 bg-card rounded-xl border p-4 shadow-sm">
			{Array.from({ length: 8 }).map((_, index) => (
				<div key={index} className="grid grid-cols-6 gap-3">
					<div className="h-4 rounded bg-muted" />
					<div className="h-4 rounded bg-muted" />
					<div className="h-4 rounded bg-muted col-span-2" />
					<div className="h-4 rounded bg-muted" />
					<div className="h-4 rounded bg-muted" />
				</div>
			))}
		</div>
	);
}

const READY_ON_OPEN_TYPES = new Set<string>([
	"title_meta",
	"title_too_short",
	"duplicate_title",
	"serp_title_truncated",
	"page_title_same_as_h1",
	"page_title_missing",
	"page_title_over_60_chars",
	"page_title_below_30_chars",
	"duplicate_meta",
	"serp_meta_truncated",
	"meta_missing",
	"meta_description_over_155_chars",
	"meta_description_below_70_chars",
	"h1",
	"h1_missing",
	"h1_multiple",
	"h1_too_long",
	"duplicate_h1",
]);

const RUN_ID_UUID_RE =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// A FastAPI error `detail` can be a string, an object, or — on a 422 validation
// error — an ARRAY of { type, loc, msg, input, ctx }. Rendering a non-string as a
// React child throws (minified React error #31), which is what white-screened the
// page when a malformed run_id produced a 422. Always coerce to a readable string.
function detailToText(detail: unknown): string {
	if (typeof detail === "string") return detail;
	if (Array.isArray(detail)) {
		return detail
			.map((d) => {
				if (d && typeof d === "object") {
					const item = d as { loc?: unknown; msg?: unknown };
					const loc = Array.isArray(item.loc)
						? item.loc.filter((p) => p !== "body").join(".")
						: "";
					const msg = typeof item.msg === "string" ? item.msg : "";
					return loc && msg ? `${loc}: ${msg}` : msg || loc;
				}
				return typeof d === "string" ? d : "";
			})
			.filter(Boolean)
			.join("; ");
	}
	if (detail && typeof detail === "object") {
		const msg = (detail as { msg?: unknown }).msg;
		return typeof msg === "string" ? msg : "";
	}
	return "";
}

function getIssueLoadError(error: unknown) {
	const err = error as {
		code?: string;
		message?: string;
		response?: { status?: number; data?: { detail?: unknown } };
	};
	const status = err.response?.status;
	const detailText = detailToText(err.response?.data?.detail);
	const messageText = err.message || "";
	if (err.code === "ECONNABORTED" || messageText.toLowerCase().includes("timeout")) {
		return {
			title: "Technical Audit API timed out",
			description:
				"The backend responded too slowly for this audit dataset. Try a smaller page size or retry after the server catches up.",
		};
	}
	if (!status || messageText.toLowerCase().includes("network")) {
		return {
			title: "Technical Audit API unavailable",
			description:
				"The frontend cannot reach the backend. For local testing, confirm start.sh --db dev is running and the API health endpoint is reachable.",
		};
	}
	if (status === 422) {
		return {
			title: "This audit run link looks invalid",
			description:
				"The run ID in the URL is malformed (it may be a truncated or outdated link). Open the run again from the Site Audit list.",
		};
	}
	return {
		title: `Failed to load issues${status ? ` (${status})` : ""}`,
		description: detailText || messageText || "Unknown error occurred",
	};
}

interface PaginationControlsProps {
	page: number;
	pageSize: number;
	total: number;
	totalPages: number;
	onPageChange: (page: number) => void;
	onPageSizeChange: (pageSize: number) => void;
	isFetching?: boolean;
	/** Embed inline (no card chrome) — e.g. inside a table toolbar. */
	bare?: boolean;
}

function PaginationControls({
	page,
	pageSize,
	total,
	totalPages,
	onPageChange,
	onPageSizeChange,
	isFetching = false,
	bare = false,
}: PaginationControlsProps) {
	const safeTotalPages = Math.max(totalPages || 1, 1);
	const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
	const end = Math.min(page * pageSize, total);

	return (
		<div
			className={cn(
				"flex flex-wrap items-center gap-3 text-sm",
				bare
					? "justify-end"
					: "justify-between bg-card border rounded-xl px-4 py-3 shadow-sm",
			)}
		>
			<div className="text-muted-foreground tabular-nums">
				<span className="font-medium text-foreground">{start}</span>–
				<span className="font-medium text-foreground">{end}</span> of{" "}
				<span className="font-medium text-foreground">{total}</span>
				{isFetching && <span className="ml-2 text-coral">Refreshing…</span>}
			</div>
			<div className="flex items-center gap-2">
				<label className="text-muted-foreground" htmlFor="technical-audit-page-size">
					Rows
				</label>
				<select
					id="technical-audit-page-size"
					value={pageSize}
					onChange={(event) => onPageSizeChange(Number(event.target.value))}
					className="h-9 rounded-md border border-border bg-card px-2 text-sm"
				>
					<option value={10}>10</option>
					<option value={20}>20</option>
					<option value={50}>50</option>
				</select>
				<Button
					icon={<ChevronLeft className="w-4 h-4" />}
					disabled={page <= 1}
					onClick={() => onPageChange(page - 1)}
				/>
				<span className="min-w-[100px] text-center text-muted-foreground">
					Page {page} of {safeTotalPages}
				</span>
				<Button
					icon={<ChevronRight className="w-4 h-4" />}
					disabled={page >= safeTotalPages}
					onClick={() => onPageChange(page + 1)}
				/>
			</div>
		</div>
	);
}

// Filter Chip Component
interface FilterChipProps {
	label: string;
	isActive: boolean;
	onClick: () => void;
	count?: number;
}

function FilterChip({ label, isActive, onClick, count }: FilterChipProps) {
	return (
		<button
			onClick={onClick}
			className={cn(
				"inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all",
				"border hover:shadow-sm",
				isActive
					? "bg-gray-900 text-white border-gray-900 shadow-sm"
					: "bg-card text-foreground border-border hover:border-border hover:bg-muted",
			)}
		>
			{label}
			{count !== undefined && (
				<span
					className={cn(
						"text-xs px-1.5 py-0.5 rounded-full",
						isActive ? "bg-card/20" : "bg-muted text-muted-foreground",
					)}
				>
					{count}
				</span>
			)}
		</button>
	);
}

// Active Filter Chips — pill row showing only set filters with × to clear each
interface ActiveFilterChipsProps {
	filters: IssueFilters;
	searchQuery: string;
	onClearFilter: (key: "type" | "severity" | "status") => void;
	onClearSearch: () => void;
	onClearAll: () => void;
}

function ActiveFilterChips({
	filters,
	searchQuery,
	onClearFilter,
	onClearSearch,
	onClearAll,
}: ActiveFilterChipsProps) {
	const chips: Array<{
		key: string;
		label: string;
		onClear: () => void;
	}> = [];

	if (filters.type) {
		chips.push({
			key: "type",
			label: `Type: ${getIssueTypeFilterLabel(filters.type)}`,
			onClear: () => onClearFilter("type"),
		});
	}
	if (filters.severity) {
		chips.push({
			key: "severity",
			label: `Severity: ${filters.severity}`,
			onClear: () => onClearFilter("severity"),
		});
	}
	if (filters.status) {
		chips.push({
			key: "status",
			label: `Status: ${filters.status}`,
			onClear: () => onClearFilter("status"),
		});
	}
	if (searchQuery) {
		chips.push({
			key: "search",
			label: `Search: "${searchQuery}"`,
			onClear: onClearSearch,
		});
	}

	if (chips.length === 0) return null;

	return (
		<div className="flex flex-wrap items-center gap-2">
			{chips.map((chip) => (
				<button
					key={chip.key}
					type="button"
					onClick={chip.onClear}
					className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-coral-light text-coral-dark text-xs font-medium hover:bg-coral hover:text-white transition-colors"
				>
					<span>{chip.label}</span>
					<X className="w-3 h-3" />
				</button>
			))}
			{chips.length >= 2 && (
				<button
					type="button"
					onClick={onClearAll}
					className="text-xs font-medium text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
				>
					Clear all
				</button>
			)}
		</div>
	);
}

interface PillFiltersProps {
	filters: IssueFilters;
	summary: IssuesSummary;
	onFilterChange: (filters: Partial<IssueFilters>) => void;
	searchQuery: string;
	onSearchChange: (query: string) => void;
	onSearchSubmit: () => void;
	canSubmitSearch: boolean;
}

function PillFilters({
	filters,
	summary,
	onFilterChange,
	searchQuery,
	onSearchChange,
	onSearchSubmit,
	canSubmitSearch,
}: PillFiltersProps) {
	const [issueTypeOpen, setIssueTypeOpen] = useState(false);
	const hasActiveFilters = filters.type || filters.severity || filters.status;

	// Calculate counts for each filter option
	const typeCounts = useMemo(() => {
		const counts: Partial<Record<IssueType, number>> = {};
		for (const [type, count] of Object.entries(summary.by_type)) {
			counts[type as IssueType] = count;
		}
		return counts;
	}, [summary.by_type]);
	const issueTypeOptions = useMemo(
		() =>
			// All backend issue types are reachable as filter options — the old
			// surfaced-type allowlist is gone, so no type is hidden here.
			(Object.keys(ISSUE_TYPE_LABELS) as IssueType[])
				.map((type) => [type, getIssueTypeFilterLabel(type)] as [IssueType, string])
				.sort((a, b) => a[1].localeCompare(b[1])),
		[],
	);

	const statusCounts = useMemo(() => {
		const counts: Partial<Record<IssueStatus, number>> = {};
		for (const [status, count] of Object.entries(summary.by_status)) {
			counts[status as IssueStatus] = count;
		}
		return counts;
	}, [summary.by_status]);

	return (
		<div className="px-6 py-4 bg-muted/50 border-b">
			<div className="flex flex-col gap-4">
				{/* Search and Clear */}
				<div className="flex items-center gap-3">
					<div className="relative flex-1 max-w-md">
						<Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
						<input
							type="text"
							placeholder="Search by page URL..."
							value={searchQuery}
							onChange={(e) => onSearchChange(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									onSearchSubmit();
								}
							}}
							className={cn(
								"w-full pl-9 pr-4 py-2 text-sm rounded-lg border bg-card",
								"focus:outline-none focus:ring-2 focus:ring-coral/20 focus:border-coral",
								"border-border placeholder:text-muted-foreground",
							)}
						/>
					</div>
					<button
						type="button"
						onClick={onSearchSubmit}
						disabled={!canSubmitSearch}
						className={cn(
							"inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors",
							canSubmitSearch
								? "bg-card border-border text-foreground hover:border-coral hover:text-coral"
								: "bg-muted border-border text-muted-foreground cursor-not-allowed",
						)}
					>
						<Search className="h-4 w-4" />
						Search
					</button>
					{hasActiveFilters && (
						<button
							onClick={() => onFilterChange({ type: undefined, severity: undefined, status: undefined })}
							className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
						>
							<X className="h-4 w-4" />
							Clear filters
						</button>
					)}
				</div>

				{/* Filter Groups */}
				<div className="flex flex-wrap items-center gap-6">
					{/* Issue Type Filters */}
					<div className="flex items-center gap-2">
						<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
							Type
						</span>
						<Popover open={issueTypeOpen} onOpenChange={setIssueTypeOpen}>
							<PopoverTrigger asChild>
								<button
									type="button"
									role="combobox"
									aria-expanded={issueTypeOpen}
									className="inline-flex h-9 w-[300px] items-center justify-between rounded-lg border border-border bg-card px-3 text-sm font-medium text-foreground shadow-sm hover:border-border"
								>
										<span className="truncate">
											{filters.type
												? `${getIssueTypeFilterLabel(filters.type)} (${typeCounts[filters.type] || 0})`
												: `All issue types (${summary.total})`}
										</span>
										<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
									</button>
							</PopoverTrigger>
							<PopoverContent className="w-[340px] p-0" align="start">
								<Command>
									<CommandInput placeholder="Search issue types..." />
									<CommandList>
										<CommandEmpty>No issue type found.</CommandEmpty>
										<CommandGroup>
											<CommandItem
												value="all issue types"
												onSelect={() => {
													onFilterChange({ type: undefined });
													setIssueTypeOpen(false);
												}}
											>
												<Check
													className={cn(
														"mr-2 h-4 w-4",
														!filters.type ? "opacity-100" : "opacity-0",
													)}
												/>
												<span className="flex-1">All issue types</span>
												<span className="text-xs text-muted-foreground">{summary.total}</span>
											</CommandItem>
											{issueTypeOptions.map(([type, label]) => (
											<CommandItem
												key={type}
												value={getIssueTypeFilterSearchValue(type)}
												onSelect={() => {
														onFilterChange({ type });
														setIssueTypeOpen(false);
													}}
												>
													<Check
														className={cn(
															"mr-2 h-4 w-4",
															filters.type === type ? "opacity-100" : "opacity-0",
														)}
													/>
													<span className="flex-1">{label}</span>
													<span className="text-xs text-muted-foreground">
														{typeCounts[type] || 0}
													</span>
												</CommandItem>
											))}
										</CommandGroup>
									</CommandList>
								</Command>
							</PopoverContent>
						</Popover>
					</div>

					{/* Severity Filters */}
					<div className="flex items-center gap-2">
						<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
							Severity
						</span>
						<div className="flex items-center gap-1.5">
							<FilterChip
								label="All"
								isActive={!filters.severity}
								onClick={() => onFilterChange({ severity: undefined })}
								count={summary.total}
							/>
							<FilterChip
								label="Critical"
								isActive={filters.severity === "critical"}
								onClick={() => onFilterChange({ severity: "critical" })}
								count={summary.by_severity.critical}
							/>
							<FilterChip
								label="Warning"
								isActive={filters.severity === "warning"}
								onClick={() => onFilterChange({ severity: "warning" })}
								count={summary.by_severity.warning}
							/>
							<FilterChip
								label="Info"
								isActive={filters.severity === "info"}
								onClick={() => onFilterChange({ severity: "info" })}
								count={summary.by_severity.info}
							/>
						</div>
					</div>

					{/* Status Filters */}
					<div className="flex items-center gap-2">
						<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
							Status
						</span>
						<div className="flex items-center gap-1.5">
							<FilterChip
								label="All"
								isActive={!filters.status}
								onClick={() => onFilterChange({ status: undefined })}
								count={summary.total}
							/>
							<FilterChip
								label="Pending"
								isActive={filters.status === "pending"}
								onClick={() => onFilterChange({ status: "pending" })}
								count={statusCounts.pending || 0}
							/>
							<FilterChip
								label="Suggested"
								isActive={filters.status === "approved"}
								onClick={() => onFilterChange({ status: "approved" })}
								count={statusCounts.approved || 0}
							/>
							<FilterChip
								label="Applied"
								isActive={filters.status === "applied"}
								onClick={() => onFilterChange({ status: "applied" })}
								count={statusCounts.applied || 0}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default function TechnicalAuditIssuesPage() {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const { currentTenant: currentTenantId } = useTenantStore();
	const { workspaces, setCurrentWorkspace, selectTenantByWebsite } = useWorkspaceStore();

	// Get run_id from URL params. A malformed run_id (e.g. a truncated UUID from a
	// stale link) would fire a request that 422s; treat it as "no valid run" so we
	// surface a clear message instead of firing the doomed request.
	const rawRunId = searchParams.get("run_id");
	const runIdMalformed = !!rawRunId && !RUN_ID_UUID_RE.test(rawRunId);
	const runId = runIdMalformed ? null : rawRunId;

	// Filters and pagination state (seed type filter from a deep-link, e.g. the
	// overview "Fix →" links navigate with ?view=issues&type=alt_text)
	const [filters, setFilters] = useState<IssueFilters>(() => {
		const t = searchParams.get("type");
		return t ? { type: t } : {};
	});
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [searchQuery, setSearchQuery] = useState("");

	// Selected issue for drawer
	const [selectedIssue, setSelectedIssue] = useState<PageIssue | null>(null);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [viewMode, setViewMode] = useState<
		"overview" | "by_issue" | "by_page" | "tickets" | "duplicates"
	>(
		() => {
			const v = searchParams.get("view");
			// Deep links pick the tab: ?view=tickets → Tickets board (ticket
			// notifications link here), ?view=duplicates → consolidated duplicate
			// groups (BDD A5), ?view=issues (or a ?type= filter) → Issues; else
			// Overview.
			if (v === "tickets") return "tickets";
			if (v === "duplicates") return "duplicates";
			return v === "issues" || searchParams.get("type") ? "by_issue" : "overview";
		},
	);
	const [applyPreviewIssue, setApplyPreviewIssue] = useState<PageIssue | null>(null);
	const [applyPreviewOpen, setApplyPreviewOpen] = useState(false);
	// Specialist-edited fix value (e.g. an edited destination URL) carried from
	// the drawer through the apply-preview dialog into the apply request.
	const [applyPreviewEditedValue, setApplyPreviewEditedValue] = useState<
		ProposedFix | undefined
	>(undefined);
	// missing_from_sitemap: the user's explicit site-wide confirmation, carried
	// from the drawer's confirmation checkbox through the apply-preview dialog
	// into the apply body as `confirm_site_wide`. Never set from anywhere else —
	// a site-wide sitemap change is impossible without that deliberate tick.
	const [applyPreviewConfirmSiteWide, setApplyPreviewConfirmSiteWide] =
		useState(false);
	// Verify-result modal: the issue that was verified + the verify API response.
	const [verifyResultIssue, setVerifyResultIssue] = useState<PageIssue | null>(
		null,
	);
	const [verifyResult, setVerifyResult] = useState<VerifyFixResult | null>(null);
	const [verifyModalOpen, setVerifyModalOpen] = useState(false);

	// UI-3: condition-builder is the single filter source for the Issues tab.
	// Seed from a deep-link (?type=alt_text). compiled maps to backend params;
	// all issue types are reachable (the old surfaced-type allowlist is gone).
	const [conditions, setConditions] = useState<FilterCondition[]>(() => {
		const t = searchParams.get("type");
		return t ? [{ id: newConditionId(), field: "type", operator: "is", value: t }] : [];
	});
	const compiled = compileConditions(conditions);

	// URL search box (top toolbar) — debounced into the issues query.
	const [urlSearch, setUrlSearch] = useState("");
	const [searchTerm, setSearchTerm] = useState("");
	const [searchPending, setSearchPending] = useState(false);
	useEffect(() => {
		const t = setTimeout(() => {
			const raw = urlSearch.trim();
			const lower = raw.toLowerCase();
			// If the typed text contains an issue-type label (e.g. "Missing Image
			// Alt Text" contains "Image Alt Text"), search by that type key.
			// Longest matching label wins; otherwise fall back to raw text (the
			// backend matches URL/title/type anyway).
			let typeKey: string | undefined;
			let best = 0;
			for (const [key, label] of Object.entries(ISSUE_TYPE_LABELS)) {
				const l = String(label).toLowerCase();
				if (l && lower.includes(l) && l.length > best) {
					typeKey = key;
					best = l.length;
				}
			}
			setSearchTerm(typeKey ?? raw);
			setPage(1);
			setSearchPending(false);
		}, 350);
		return () => clearTimeout(t);
	}, [urlSearch]);

	// Fetch issues
	const {
		data: issuesResponse,
		isLoading,
		isFetching,
		error,
		refetch,
	} = useTechnicalAuditIssues(runId || "", currentTenantId, {
		filters: {
			severity: compiled.severity,
			status: compiled.status,
			search: searchTerm || compiled.search,
		},
		page,
		pageSize,
		includeTypes: compiled.includeTypes,
		excludeTypes: compiled.excludeTypes,
	});

	// Fetch run metadata (website, dates, status) for the header
	const { data: run } = useAuditRun(runId);
	const { data: selectedTenantRuns } = useAuditRuns(currentTenantId ?? null, 1, 20);

	// Ticket-notification deep links carry the ticket's workspace as ?tenant_id=…
	// so the link opens in the RIGHT workspace regardless of the viewer's last
	// selection. Align the active workspace to it before the mismatch redirect
	// below can bounce us off the linked run.
	const urlTenant = searchParams.get("tenant_id");
	useEffect(() => {
		if (!urlTenant || urlTenant === currentTenantId) return;
		const ws = workspaces.find((w) => w.website === urlTenant);
		if (ws) {
			// Updates the workspace switcher label + bridges to the tenant store.
			setCurrentWorkspace(ws);
		} else {
			// Viewer isn't a member of a matching workspace (or none is loaded yet);
			// select the explicit unlinked kind so data still loads for the link's
			// workspace.
			selectTenantByWebsite(urlTenant);
		}
	}, [urlTenant, currentTenantId, workspaces, setCurrentWorkspace, selectTenantByWebsite]);

	useEffect(() => {
		if (!runId || !currentTenantId || !run) return;
		// Don't bounce while we're still aligning to a deep link's ?tenant_id —
		// wait for the workspace switch above to land, else we'd redirect off the
		// linked run before its workspace is active.
		if (urlTenant && urlTenant !== currentTenantId) return;
		if (run.tenant_id === currentTenantId) return;
		if (!selectedTenantRuns) return;

		const latestSelectedTenantRun = selectedTenantRuns.runs[0];
		if (latestSelectedTenantRun) {
			if (latestSelectedTenantRun.id !== runId) {
				navigate(
					`/audit/technical-issues?run_id=${latestSelectedTenantRun.id}`,
					{ replace: true },
				);
			}
			return;
		}

		navigate("/audit/technical", { replace: true });
	}, [currentTenantId, navigate, run, runId, selectedTenantRuns, urlTenant]);

	// Prefer the fresh fetched list by id, except immediately after Generate fix:
	// the local selected row can contain the new suggestion before the list refetch
	// catches up. In that short window, keep the generated row so the drawer action
	// and preview receive the selected source-page payload.
	const liveSelectedIssue = useMemo(() => {
		if (!selectedIssue) return null;
		const fresh = (issuesResponse?.issues || []).find(
			(item) => item.id === selectedIssue.id,
		);
		if (
			fresh &&
			hasGeneratedSuggestion(selectedIssue) &&
			!hasGeneratedSuggestion(fresh)
		) {
			return selectedIssue;
		}
		return fresh ?? selectedIssue;
	}, [issuesResponse?.issues, selectedIssue]);

	// Reset the drawer/preview selection whenever the run, filters, search, or
	// pagination identity changes. Without this the drawer could keep rendering a
	// row from a prior snapshot whose issue_type no longer matches the live data.
	useEffect(() => {
		setSelectedIssue(null);
		setDrawerOpen(false);
		setApplyPreviewIssue(null);
		setApplyPreviewOpen(false);
		setApplyPreviewEditedValue(undefined);
		setApplyPreviewConfirmSiteWide(false);
	}, [runId, filters, page, pageSize]);

	// Selection state for bulk operations (needs issue IDs from query)
	const allIssueIds = (issuesResponse?.issues || []).map((i) => i.id);
	const selection = useIssueSelection(allIssueIds);

	// Apply fixes mutation
	const applySingleFix = useApplySingleFix();
	const reverifyStale = useReverifyStale();
	const { data: ticketsData } = useManualFixTickets(currentTenantId);
	const openTicketCount = ticketsData?.counts?.open ?? 0;
	const verifyFix = useVerifyFix();
	const generateSuggestion = useGenerateIssueSuggestion();
	const bulkUpdate = useBulkUpdateIssues();
	const updateIssue = useUpdateIssue();

	// Shared verify handler: run verifyFix, then open the VerifyResultModal with
	// the verified issue + response (BEFORE/AFTER + evidence). Still refetches so
	// the list reflects the new fix_status. Used by both the table row action and
	// the issue drawer.
	const handleVerifyIssue = useCallback(
		(issueId: string) => {
			const issue =
				(issuesResponse?.issues || []).find((i) => i.id === issueId) ?? null;
			verifyFix.mutate(issueId, {
				onSuccess: (data) => {
					setVerifyResultIssue(issue);
					setVerifyResult(data);
					setVerifyModalOpen(true);
					refetch();
				},
			});
		},
		[issuesResponse?.issues, verifyFix, refetch],
	);

	// Clear a failed row's red "Failed" badge: PATCH fix_status back to pending
	// so the row is actionable again (retry/apply), then refetch so the badge
	// updates. Escapes the previous "Verify"-only dead-end for failed applies.
	const handleDismissError = useCallback(
		(issueId: string) => {
			updateIssue.mutate(
				{ issueId, data: { fixStatus: "pending" } },
				{ onSuccess: () => refetch() },
			);
		},
		[updateIssue, refetch],
	);

	// Handle filter changes
	const handleFilterChange = useCallback(
		(newFilters: Partial<IssueFilters>) => {
			setFilters((prev) => ({ ...prev, ...newFilters }));
			setPage(1); // Reset to first page on filter change
			selection.clear(); // Clear selection when filters change
		},
		[selection],
	);

	// UI-3: condition-builder changes (single source for the Issues tab)
	const handleConditionsChange = useCallback(
		(next: FilterCondition[]) => {
			setConditions(next);
			setPage(1);
			selection.clear();
		},
		[selection],
	);

	// Handle search changes
	const handleSearchChange = useCallback((query: string) => {
		setSearchQuery(query);
	}, []);

	const handleSearchSubmit = useCallback(() => {
		const nextSearch = searchQuery.trim();
		setSearchQuery(nextSearch);
		setFilters((prev) => ({
			...prev,
			search: nextSearch || undefined,
		}));
		setPage(1);
		selection.clear();
	}, [searchQuery, selection]);

	const handleSearchClear = useCallback(() => {
		setSearchQuery("");
		setFilters((prev) => ({ ...prev, search: undefined }));
		setPage(1);
		selection.clear();
	}, [selection]);

	const handlePageChange = useCallback(
		(nextPage: number) => {
			setPage(Math.max(1, nextPage));
			selection.clear();
		},
		[selection],
	);

	const handlePageSizeChange = useCallback(
		(nextPageSize: number) => {
			setPageSize(nextPageSize);
			setPage(1);
			selection.clear();
		},
		[selection],
	);

	// Handle row click to open drawer
	const handleRowClick = useCallback(
		(issue: PageIssue) => {
			setSelectedIssue(issue);
			setDrawerOpen(true);
			if (
				READY_ON_OPEN_TYPES.has(issue.issue_type) &&
				!(issue.approved_value || issue.proposed_value || issue.proposed_fix)
			) {
				generateSuggestion.mutate({ issueId: issue.id, force: false });
			}
		},
		[generateSuggestion.mutate],
	);

	// Handle drawer close
	const handleDrawerClose = useCallback(() => {
		setDrawerOpen(false);
		// Delay clearing the issue to allow close animation
		setTimeout(() => setSelectedIssue(null), 300);
	}, []);

	// Resolve a row ONLY from the fresh fetched list (never the frozen
	// `selectedIssue`): after a re-crawl/filter/pagination it can be a prior-run
	// row with a now-wrong issue_type, which would target the wrong fix.
	const resolveFreshIssue = useCallback(
		(issueId: string): PageIssue | null => {
			const issue = (issuesResponse?.issues || []).find(
				(item) => item.id === issueId,
			);
			if (
				selectedIssue?.id === issueId &&
				hasGeneratedSuggestion(selectedIssue) &&
				(!issue || !hasGeneratedSuggestion(issue))
			) {
				return selectedIssue;
			}
			if (!issue) {
				message.warning(
					"This issue is no longer in the current view (the list is out of date). Refreshing — re-open and Apply again.",
				);
				refetch();
				return null;
			}
			return issue;
		},
		[issuesResponse?.issues, refetch, selectedIssue],
	);

	// Optional inspection: open the before/after preview modal (does NOT apply).
	// This handler ONLY surfaces the ApplyFixPreviewDialog for review; the actual
	// live-WordPress mutation happens via handleConfirmApplyIssue (the dialog's
	// Confirm) or handleApplyIssueDirect (direct-apply types). Never call
	// applySingleFix.mutate() here — a preview must not write to WordPress.
	const handleOpenApplyPreview = useCallback(
		(
			issueId: string,
			editedValue?: ProposedFix,
			opts?: { confirm_site_wide?: boolean },
		) => {
			const issue = resolveFreshIssue(issueId);
			if (!issue) {
				setApplyPreviewOpen(false);
				setApplyPreviewIssue(null);
				setApplyPreviewEditedValue(undefined);
				setApplyPreviewConfirmSiteWide(false);
				return;
			}
			setApplyPreviewIssue(issue);
			setApplyPreviewEditedValue(editedValue);
			// Carried from the drawer's explicit site-wide confirmation checkbox
			// (missing_from_sitemap). Absent = false: the apply body then omits
			// confirm_site_wide entirely and the backend refuses any site-wide write.
			setApplyPreviewConfirmSiteWide(Boolean(opts?.confirm_site_wide));
			setApplyPreviewOpen(true);
		},
		[resolveFreshIssue],
	);

		// Direct apply — no blocking confirm modal ("just SEND IT"). Uses the current
		// proposed/legacy-approved/edited value; backend stores legacy state internally.
	const handleApplyIssueDirect = useCallback(
		(
			issueId: string,
			editedValue?: ProposedFix,
			opts?: { force?: boolean; confirm_site_wide?: boolean },
		) => {
			const issue = resolveFreshIssue(issueId);
			if (!issue) return;
			applySingleFix.mutate(
				{
					issueId,
					data: {
						...(editedValue ? { approvedValue: editedValue } : {}),
						...(opts?.force ? { force: true } : {}),
						...(opts?.confirm_site_wide ? { confirm_site_wide: true } : {}),
					},
				},
				{
					onSuccess: (data) => {
						refetch();
						if (data.success) handleDrawerClose();
					},
				},
			);
		},
		[resolveFreshIssue, applySingleFix, refetch, handleDrawerClose],
	);

	const handleRequestApplyIssue = useCallback(
		(
			issueId: string,
			editedValue?: ProposedFix,
			opts?: { force?: boolean; confirm_site_wide?: boolean },
		) => {
			const issue = resolveFreshIssue(issueId);
			if (!issue) return;
			// "Apply anyway" (review-only override) is already confirmed in the
			// drawer — apply directly with force, no preview-confirm step.
			if (opts?.force) {
				handleApplyIssueDirect(issueId, editedValue, { force: true });
				return;
			}
			// H1 auto-fixes apply straight to WordPress with no confirm modal; all
			// other types keep the preview-confirm step.
			if (isDirectApplyIssueType(issue.issue_type)) {
				handleApplyIssueDirect(issueId, editedValue);
				return;
			}
			handleOpenApplyPreview(issueId, editedValue, {
				confirm_site_wide: opts?.confirm_site_wide,
			});
		},
		[resolveFreshIssue, handleApplyIssueDirect, handleOpenApplyPreview],
	);

	const handleConfirmApplyIssue = useCallback(
		// `dimensions` is set only for image_missing_dimensions when the specialist
		// edited the measured width/height in the preview dialog — it posts as
		// top-level {width, height} so the backend writes exactly those values.
		(issueId: string, dimensions?: { width: number; height: number }) => {
			applySingleFix.mutate(
				{
					issueId,
					data: {
						...(applyPreviewEditedValue
							? { approvedValue: applyPreviewEditedValue }
							: {}),
						...(dimensions ?? {}),
						// missing_from_sitemap: only ever true when the drawer's site-wide
						// confirmation checkbox was ticked before Apply was pressed.
						...(applyPreviewConfirmSiteWide
							? { confirm_site_wide: true }
							: {}),
					},
				},
				{
					onSuccess: (data) => {
						if (data.success) {
							setApplyPreviewOpen(false);
							setApplyPreviewIssue(null);
							setApplyPreviewEditedValue(undefined);
							setApplyPreviewConfirmSiteWide(false);
						}
						refetch();
					},
				},
			);
		},
		[
			applySingleFix,
			refetch,
			applyPreviewEditedValue,
			applyPreviewConfirmSiteWide,
		],
	);

	// Handle CSV export
	const handleExportCSV = useCallback(() => {
		const issues = issuesResponse?.issues || [];
		if (issues.length === 0) {
			message.warning("No issues to export");
			return;
		}
		exportCSV(issues, currentTenantId || "audit");
		message.success("CSV exported");
	}, [issuesResponse?.issues, currentTenantId]);

	// Handle PDF export
	const handleExportPDF = useCallback(() => {
		const issues = issuesResponse?.issues || [];
		const summary = issuesResponse?.summary;
		if (!summary || issues.length === 0) {
			message.warning("No issues to export");
			return;
		}
		exportPDF(issues, summary, currentTenantId || "audit");
		message.success("PDF exported");
	}, [issuesResponse?.issues, issuesResponse?.summary, currentTenantId]);

	// If no run_id, show message
	if (runIdMalformed) {
		return (
			<div className="p-6">
				<Alert
					type="error"
					message="This audit run link looks invalid"
					description="The run ID in the URL is malformed (it may be a truncated or outdated link). Open the run again from the Site Audit list."
					action={
						<Button onClick={() => navigate("/audit/technical")}>
							Go to Site Audit
						</Button>
					}
				/>
			</div>
		);
	}

	if (!runId) {
		return (
			<div className="p-6">
				<Alert
					type="warning"
					message="No audit run selected"
					description="Please run a site audit first to view and manage issues."
					action={
						<Button onClick={() => navigate("/audit/technical")}>
							Go to Site Audit
						</Button>
					}
				/>
			</div>
		);
	}

	// Loading state
	if (isLoading) {
		return (
			<div className="p-6">
				<IssuesLoadingSkeleton />
			</div>
		);
	}

	// Error state
	if (error) {
		const loadError = getIssueLoadError(error);
		return (
			<div className="p-6">
				<Alert
					type="error"
					message={loadError.title}
					description={loadError.description}
					action={<Button onClick={() => refetch()}>Retry</Button>}
				/>
			</div>
		);
	}

	const issues = issuesResponse?.issues || [];
	const summary = issuesResponse?.summary;
	const totalIssues = issuesResponse?.total ?? summary?.total ?? issues.length;
	const totalPages = issuesResponse?.total_pages ?? Math.max(Math.ceil(totalIssues / pageSize), 1);
	const hasActiveFilters = conditions.some(isActiveCondition);

	// UI-3: builder option lists (all issue types are reachable here).
	// The Issue Type dropdown must offer every type present in the RUN. `summary.by_type`
	// is scoped by the ACTIVE type filter — once a type is selected the summary counts
	// only that type, so filtering the options by those counts collapses the dropdown to
	// the single selected option and the user can't switch (the reported bug). So: hide
	// empty categories ONLY when no type filter is active; while a type filter IS active,
	// offer every known type (the combobox is searchable, so the fuller list is fine)
	// rather than collapsing. No hooks here — this block runs after early returns
	// (isLoading/error), so a hook would violate the Rules of Hooks (React #310).
	const typeFilterActive = (compiled.includeTypes?.length ?? 0) > 0;
	const typeCountsForFilter = (summary?.by_type ?? {}) as Record<string, number>;
	const typeOptions = Object.entries(ISSUE_TYPE_LABELS)
		.filter(([value]) => typeFilterActive || (typeCountsForFilter[value] ?? 0) > 0)
		.map(([value]) => ({
			value,
			label: getIssueTypeFilterLabel(value),
			searchValue: getIssueTypeFilterSearchValue(value),
		}))
		.sort((a, b) => a.label.localeCompare(b.label));
	const severityOptions = [
		{ value: "critical", label: "Critical" },
		{ value: "warning", label: "Warning" },
		{ value: "info", label: "Info" },
	];
	// The table hides only "obsolete" by default; every other status (incl.
	// applied/verified) stays visible. These options let a user explicitly filter
	// TO a status (e.g. only "Applied" to verify recent fixes, or "Excluded
	// (obsolete)" to review voided items).
	const statusOptions = [
		{ value: "pending", label: "Pending" },
		{ value: "approved", label: "Approved" },
		{ value: "failed", label: "Failed" },
		{ value: "manual", label: "Expert fix requested" },
		{ value: "manual_applied", label: "Fixed by expert" },
		{ value: "applied", label: "Applied" },
		{ value: "recommended", label: "Recommendation saved" },
		{ value: "verified", label: "Verified" },
		{ value: "rejected", label: "Rejected" },
		{ value: "skipped", label: "Skipped" },
		{ value: "protected", label: "Protected" },
		{ value: "obsolete", label: "Excluded (obsolete)" },
	];
	const auditFilterFields = buildAuditFilterFields(
		typeOptions,
		severityOptions,
		statusOptions,
	);

	// UI-2 overview data (severity spectrum + health + ranked fix-next)
	const severityCounts = {
		critical: summary?.by_severity?.critical ?? 0,
		warning: summary?.by_severity?.warning ?? 0,
		info: summary?.by_severity?.info ?? 0,
	};
	// Size-normalized health score (GH #596) — shared with the overview card so they
	// agree, and normalized by pages so real sites don't floor at 0/100.
	const healthScore = computeHealthScore(severityCounts, run?.pages_crawled);
	const fixNext = summary
		? Object.entries(summary.by_type)
				.sort((a, b) => (b[1] as number) - (a[1] as number))
				.slice(0, 5)
				.map(([type, count]) => ({
					issueType: type,
					label: getIssueTypeHelp(type)?.title || type,
					count: count as number,
				}))
		: [];
	const goToIssuesFiltered = (field: "severity" | "type", value: string) => {
		handleConditionsChange([
			{ id: newConditionId(), field, operator: "is", value },
		]);
		setViewMode("by_issue");
	};

	return (
		<div className="flex flex-col h-full bg-muted/30">
			{/* Header with back button and actions */}
			<div className="flex items-center justify-between px-6 py-4 bg-card border-b shadow-sm">
				<div className="flex items-center gap-4">
					<Button
						type="text"
						icon={<ArrowLeft className="w-4 h-4" />}
						onClick={() => navigate(-1)}
						className="hover:bg-muted"
					>
						Back
					</Button>
					<div className="h-6 w-px bg-gray-200" />
					<div>
						<h1 className="text-xl font-semibold text-foreground">
							{run?.website || "Audit Issues"}
						</h1>
						<div className="flex items-center gap-2 text-sm text-muted-foreground mt-0.5">
							{run?.status && (
								<>
									<span
										className={cn(
											"inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium",
											run.status === "completed" &&
												"bg-teal-light text-teal-dark",
											(run.status === "running" || run.status === "pending") &&
												"bg-coral-light text-coral-dark",
											run.status === "failed" && "bg-destructive/10 text-destructive",
										)}
									>
										{run.status === "running" || run.status === "pending"
											? "In progress"
											: run.status.charAt(0).toUpperCase() + run.status.slice(1)}
									</span>
									<span className="text-gray-300">·</span>
								</>
							)}
							{run && (
								<>
									<span>
										Audited{" "}
										{new Date(
											run.completed_at || run.started_at,
										).toLocaleDateString(undefined, {
											month: "short",
											day: "numeric",
											year: "numeric",
										})}
									</span>
									{summary && (
										<>
											<span className="text-gray-300">·</span>
											<span>
												{summary.total} {summary.total === 1 ? "issue" : "issues"}
											</span>
										</>
									)}
								</>
							)}
							{!run && summary && (
								<span>{summary.total} total issues found</span>
							)}
						</div>
					</div>
				</div>

				<div className="flex items-center gap-3">
					<AuditExportSplitButton
						onExportCSV={handleExportCSV}
						onExportPDF={handleExportPDF}
					/>

					<Button
						icon={
							reverifyStale.isPending ? (
								<Loader2 className="w-4 h-4 animate-spin" />
							) : (
								<Sparkles className="w-4 h-4" />
							)
						}
						loading={reverifyStale.isPending}
						onClick={() => runId && reverifyStale.mutate(runId)}
						disabled={!runId || reverifyStale.isPending}
						className="flex items-center gap-2"
						title="Re-check open issues against the live site and close stale ones (already-fixed links, unpublished or archive pages)"
					>
						{reverifyStale.isPending ? "Re-checking…" : "Clean up stale"}
					</Button>
					<Button
						icon={<RefreshCw className="w-4 h-4" />}
						onClick={() => refetch()}
						className="flex items-center gap-2"
					>
						Refresh
					</Button>
				</div>
			</div>

			{/* View mode tabs */}
			<div className="px-6 pt-4 flex flex-wrap items-center gap-3">
				<div className="inline-flex items-center gap-1 p-1 bg-muted rounded-lg">
					<button
						type="button"
						onClick={() => setViewMode("overview")}
						className={cn(
							"px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
							viewMode === "overview"
								? "bg-card text-foreground shadow-sm"
								: "text-muted-foreground hover:text-foreground",
						)}
					>
						Overview
					</button>
					<button
						type="button"
						onClick={() => setViewMode("by_issue")}
						className={cn(
							"px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
							viewMode === "by_issue"
								? "bg-card text-foreground shadow-sm"
								: "text-muted-foreground hover:text-foreground",
						)}
					>
						Issues
					</button>
					<button
						type="button"
						onClick={() => setViewMode("by_page")}
						className={cn(
							"px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
							viewMode === "by_page"
								? "bg-card text-foreground shadow-sm"
								: "text-muted-foreground hover:text-foreground",
						)}
					>
						By Page
					</button>
					<button
						type="button"
						onClick={() => setViewMode("duplicates")}
						className={cn(
							"px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
							viewMode === "duplicates"
								? "bg-card text-foreground shadow-sm"
								: "text-muted-foreground hover:text-foreground",
						)}
					>
						Duplicates
					</button>
					<button
						type="button"
						onClick={() => setViewMode("tickets")}
						className={cn(
							"flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
							viewMode === "tickets"
								? "bg-card text-foreground shadow-sm"
								: "text-muted-foreground hover:text-foreground",
						)}
					>
						Tickets
						{openTicketCount > 0 && (
							<span className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-coral px-1.5 text-[11px] font-semibold text-white">
								{openTicketCount}
							</span>
						)}
					</button>
				</div>
			</div>

			{/* Main content area */}
			<div className="flex-1 overflow-auto px-6 py-4">
				{viewMode === "overview" ? (
					<div className="w-full pb-6">
						<AuditOverview
							website={run?.website || currentTenantId || "This site"}
							health={healthScore}
							counts={severityCounts}
							fixNext={fixNext}
							onSelectSeverity={(s) => goToIssuesFiltered("severity", s)}
							onSelectType={(t) => goToIssuesFiltered("type", t)}
						/>
					</div>
				) : viewMode === "by_issue" ? (
					<div className="bg-card rounded-xl border shadow-sm overflow-hidden">
						<div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-3">
							<div className="min-w-0 flex-1">
								<ConditionFilter
									bare
									fields={auditFilterFields}
									conditions={conditions}
									onChange={handleConditionsChange}
									onClear={() => handleConditionsChange([])}
								/>
							</div>
							<div className="relative w-64 max-w-full">
								<Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
								<input
									type="text"
									value={urlSearch}
									onChange={(e) => {
										setUrlSearch(e.target.value);
										setSearchPending(true);
									}}
									placeholder="Search URL, title, or type…"
									className="h-9 w-full rounded-md border border-border bg-card pl-8 pr-8 text-sm focus:border-coral focus:outline-none focus:ring-2 focus:ring-coral/30"
								/>
								{urlSearch && (searchPending || isFetching) ? (
									<Loader2
										aria-label="Searching"
										className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-coral"
									/>
								) : urlSearch ? (
									<button
										type="button"
										aria-label="Clear search"
										onClick={() => setUrlSearch("")}
										className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
									>
										<X className="h-4 w-4" />
									</button>
								) : null}
							</div>
						</div>
						<IssueTable
								issues={issues}
								selectedIds={selection.selectedIds}
								onSelectionChange={(ids) => {
									selection.clear();
									for (const id of ids) {
										selection.toggle(id);
									}
								}}
								onIssueClick={handleRowClick}
								onApplyIssue={handleRequestApplyIssue}
								onVerifyIssue={handleVerifyIssue}
								onDismissError={handleDismissError}
								filters={filters}
								onFiltersChange={(nextFilters) => {
									setFilters(nextFilters);
									setSearchQuery(nextFilters.search || "");
									setPage(1);
									selection.clear();
								}}
								isLoading={isLoading}
								isFetching={isFetching}
							/>
						<div className="border-t border-border px-3 py-1">
							<PaginationControls
							bare
							page={page}
							pageSize={pageSize}
							total={totalIssues}
							totalPages={totalPages}
							onPageChange={handlePageChange}
							onPageSizeChange={handlePageSizeChange}
							isFetching={isFetching}
						/>
						</div>
					</div>
					) : viewMode === "duplicates" ? (
						<DuplicateGroupsPanel runId={runId} onIssueClick={handleRowClick} />
					) : viewMode === "tickets" ? (
						<TicketsBoard tenantId={currentTenantId} />
					) : (
						<div className="bg-card rounded-xl border shadow-sm overflow-hidden">
							<IssuesByPagePanel
								issues={issues}
								issueTypeLabels={{
									...ISSUE_TYPE_LABELS,
									h1: getIssueTypeFilterLabel("h1"),
								}}
								onIssueClick={handleRowClick}
							/>
						</div>
					)}
			</div>

			{/* Bulk action bar */}
				<BulkActionBar
					selectedCount={selection.selectedCount}
					onRejectAll={() => {
						const confirmed = window.confirm(
							`Skip ${selection.selectedCount} selected ${selection.selectedCount === 1 ? "issue" : "issues"}?\n\nThis only removes them from the Technical Audit review queue. It will not change WordPress.`,
						);
						if (!confirmed) return;
						bulkUpdate.mutate(
							{ issueIds: selection.selectedIds, action: "skip" },
							{
								onSuccess: () => {
									selection.clear();
								refetch();
							},
						},
					);
				}}
				onClear={selection.clear}
				isLoading={bulkUpdate.isPending}
			/>

			{/* Diff drawer */}
			<IssueDiffDrawer
				issue={liveSelectedIssue}
				open={drawerOpen}
					onClose={handleDrawerClose}
					onReject={() => {
						if (liveSelectedIssue) {
							const confirmed = window.confirm(
								"Skip this issue?\n\nThis only removes it from the Technical Audit review queue. It will not change WordPress.",
							);
							if (!confirmed) return;
							bulkUpdate.mutate(
								{ issueIds: [liveSelectedIssue.id], action: "skip" },
								{
								onSuccess: () => {
									refetch();
									handleDrawerClose();
								},
							},
						);
					}
				}}
				onApply={handleRequestApplyIssue}
				onPreview={handleOpenApplyPreview}
					onVerify={handleVerifyIssue}
					onGenerateSuggestion={(issueId, force) => {
					generateSuggestion.mutate(
						{
								issueId,
								force:
									force ??
									Boolean(
										liveSelectedIssue?.proposed_value ||
											liveSelectedIssue?.proposed_fix,
									),
							},
						{
							onSuccess: (updatedIssue) => {
								setSelectedIssue((current) =>
									current?.id === updatedIssue.id
										? { ...current, ...updatedIssue }
										: updatedIssue,
								);
								refetch();
							},
						},
					);
				}}
				isGeneratingSuggestion={generateSuggestion.isPending}
				isApplying={applySingleFix.isPending}
			/>
			<ApplyFixPreviewDialog
				issue={applyPreviewIssue}
				editedValue={applyPreviewEditedValue}
				open={applyPreviewOpen}
				onOpenChange={(open) => {
					setApplyPreviewOpen(open);
					if (!open) {
						setApplyPreviewIssue(null);
						setApplyPreviewEditedValue(undefined);
						// Cancelling the confirm dialog drops the site-wide consent too —
						// it must be re-given on the next Apply.
						setApplyPreviewConfirmSiteWide(false);
					}
				}}
				onConfirm={handleConfirmApplyIssue}
				isApplying={applySingleFix.isPending}
				siteWideConfirmed={applyPreviewConfirmSiteWide}
				/>
			<VerifyResultModal
				issue={verifyResultIssue}
				verifyResult={verifyResult}
				open={verifyModalOpen}
				onOpenChange={(open) => {
					setVerifyModalOpen(open);
					if (!open) {
						setVerifyResultIssue(null);
						setVerifyResult(null);
					}
				}}
			/>
			</div>
		);
	}
